import { useState } from 'react'

import csrfHeaders from 'src/utils/csrfHeaders'

// Adapted from donate_v6/fiat/useRecord.js. Trimmed to v7's surface — no
// plugins yet. Save-draft creates the order server-side and returns a
// token used for downstream payment processing.

export const genericError = i18n => ({ base: [ i18n.t('errors.donation_error') ] })

const draftProps = ({
  i18n, order, donation, references, principalOnly,
  partner, partnerItem, projectId, widget, session,
  donationTags, embeddedOn, orderProps, contact, extras, monthlyUpsell,
  canadianReceipt, giftAid, pluginData,
}) => {
  // principalOnly = the donation carries no extras. Payment methods that
  // settle off-platform — deferred wire/check/pledge today, the non-fiat
  // methods (wallet/crypto/stock/DAF) when they land — support neither
  // tipping nor fee-covering. Centralized here so callers just declare
  // intent; any future computed extra stays suppressed in one place
  // instead of needing a parallel override at every such call site.
  // amount_entered is nulled too — for these flows, the donor's typed
  // fiat is aspirational (crypto: rates fluctuate between entry and
  // send, donor may also choose to send a different amount; stock/DAF:
  // the executed amount comes from the third-party processor, not the
  // donor's input). Better to leave the column NULL than to record a
  // donor-typed number that may not match what actually arrived.
  const tip = principalOnly ? 0 : donation.tip
  const coverCosts = !principalOnly && donation.state.coverCosts
  // donationAmount is gift + covered fees only (see useDonation.js);
  // the tip rides separately in tip_amount and the backend re-sums.
  // For principalOnly flows, fall through to the raw state.amount —
  // donation.donationAmount doesn't know about principalOnly and would
  // still bake in fees if the donor had toggled cover-fees on Start
  // before picking an off-platform method.
  const amount = principalOnly ? donation.state.amount : donation.donationAmount
  const amountEntered = principalOnly ? null : donation.state.amount

  // Wallet flows skip the Contact step, so donation.state.firstName etc.
  // are blank; saveDraft caller hands us the parsed billing details
  // from the wallet event under `contact` instead. PaymentElement
  // flows pass no contact override and the donation-state values flow
  // through unchanged. `address` mirrors the same shape — the
  // AddressElement on Contact dispatches `setAddress` which spreads
  // into donation.state, so it surfaces here without a special path.
  const firstName = contact?.firstName ?? donation.state.firstName
  const lastName  = contact?.lastName  ?? donation.state.lastName
  const phone     = contact?.phone     ?? donation.state.phone
  const email     = contact?.email     ?? donation.state.email
  const address   = contact?.address   ?? donation.state.address

  return {
    id:             widget.token,
    template:       widget.template,
    partner_key:    partner.key,
    external_id:    partnerItem.external_id,
    ein:            partnerItem.beneficiary?.ein,
    embedded_on:    embeddedOn,
    enable_session: !session.collect_email,
    donation_tags: donationTags,
    // donations#create resolves the locale from this param and nothing
    // else — `orders` has no locale column and the POST carries no other
    // signal — so it has to keep being sent. Same as the Stripe intents
    // POST in stripeIntents.js.
    locale:         i18n.locale,
    ...references,
    order: {
      ...order,
      // extras.draftOrderProps() aggregates each opt-in section
      // (tribute → tribute_attributes, company contribution →
      // business_name, etc.) into a single bag.
      ...(extras?.draftOrderProps?.() || {}),
      // Separate from extras because it isn't one of the Contact-step opt-in
      // sections — it needs the donation's frequency, which useDonationExtras
      // never receives. Returns {} until the panel has been shown, so orders
      // that never saw the offer send no key at all.
      ...(monthlyUpsell?.draftOrderProps?.() || {}),
      user_first_name:    firstName,
      user_last_name:     lastName,
      user_phone_number:  phone,
      email:              email,
      address,
      partner_id:         partner.id,
      external_id:        partnerItem.external_id,
      project_id:         projectId,
      shares:             donation.shares,
      currency:           donation.currency.id,
      donation_amount:    amount,
      tip_amount:         tip,
      cover_fees:         coverCosts,
      amount_entered:     amountEntered,
      canadian_receipt:   canadianReceipt?.value,
      gift_aid:           giftAid?.value,
      // Snippet-plugin data (Order#plugin_data= unwraps { __value: [...] }).
      plugin_data:        pluginData?.(),
      // Method-specific overrides — spread last so any caller's
      // orderProps can override any field above. Crypto uses this
      // for currency / donation_amount / amount_entered (the donor's
      // crypto-step entries live in useCrypto, not donation, so
      // donation.currency / .state.amount wouldn't reflect them);
      // wire / check / pledge use it for payment_source / bank_name /
      // last4 / payment_method_used. Trusted-internal-caller API;
      // do not route untrusted user input through this surface.
      ...orderProps,
    },
  }
}

const useRecord = ({
  i18n, order, widget, partner, partnerItem, projectId, pledge_id, pdt,
  session, donationTags, embeddedOn, donation, donationsPath, extras,
  monthlyUpsell, canadianReceipt, giftAid, pluginData,
}) => {
  const [ references, setReferences ] = useState({ pledge_id, pdt })

  const saveDraft = async ({ principalOnly, order: orderProps, contact } = {}) => {
    const params = draftProps({
      i18n, order, donation, references, principalOnly,
      partner, partnerItem, projectId, widget, session,
      donationTags, embeddedOn, orderProps, contact, extras, monthlyUpsell,
      canadianReceipt, giftAid, pluginData,
    })

    try {
      const response = await fetch(donationsPath, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify(params),
      })
      const json = await response.json()

      if (response.ok) {
        const { token, order: serializedDonation } = json
        setReferences({ order_id: token })
        return { token, serializedDonation }
      }

      // donations#create renders @order.errors directly, so the body is
      // a field-keyed errors hash (e.g. { base: [...], user_first_name:
      // [...] }). Only `base` belongs on the payment-step alert; field
      // errors should have surfaced on the Contact / Amount steps. Fall
      // back to a generic message so the donor sees *something* instead
      // of a silent overlay drop when only field errors come back.
      return {
        errors: json.base?.length ? { base: json.base } : genericError(i18n),
      }
    } catch (error) {
      console.error('Error saving draft:', error)
      return { errors: genericError(i18n) }
    }
  }

  // Drop every reference on "donate again" (App calls this) so the next
  // saveDraft is a clean, unattributed order rather than a continuation:
  // a stale `order_id` would re-PATCH the just-completed order, and a
  // stale `pdt` / `pledge_id` would re-link the new gift to an already
  // -redeemed pledge. The backend guards all three now, so this is
  // belt-and-suspenders — but it keeps the request honest.
  const reset = () => setReferences(null)

  return { saveDraft, reset }
}

export default useRecord
